/* ============================================================
   Verity article post-processor
   --------------------------------------------------------------
   Verity emits article bodies with two non-semantic conventions
   we want to upgrade to first-class HTML for the new theme:

     1. Inline citation markers — bare [1] or [1,2] text inside <p>.
        We convert each numeric token to a <sup><a class="verity-citation"
        href="#src{n}">[n]</a></sup>, with smooth-scroll anchoring
        to the sources appendix.

     2. Sources appendix — Verity emits a <h2>Sources</h2> followed
        by flat <p>[n] Author — Title (Journal, Year) (type) — URL</p>
        paragraphs. We parse those into an <ol class="verity-sources-list">
        with structured rows + a type chip.

   Idempotent: safe to run multiple times. Only operates on elements
   inside the [data-verity-article] container, so it never touches
   legacy article bodies or non-Verity content.
   ============================================================ */

(function () {
  'use strict';

  const SOURCE_LINE_RE = /^\s*\[(\d+)\]\s*(.+?)\s+\((peer-review|guideline|industry|news|preprint)\)\s*(?:—|--|-)\s*(https?:\/\/\S+)\s*$/i;
  const SOURCE_LINE_FALLBACK_RE = /^\s*\[(\d+)\]\s*(.+)$/;
  const CITATION_RE = /\[(\d+(?:\s*,\s*\d+)*)\]/g;

  function init() {
    const root = document.querySelector('[data-verity-article]');
    if (!root) return;
    const body = root.querySelector('.verity-article__body');
    if (!body) return;
    if (body.dataset.verityProcessed === 'true') return;
    body.dataset.verityProcessed = 'true';

    upgradeSourcesSection(body);
    injectCitationLinks(body);
  }

  // ---- (1) Sources section parsing ----
  function upgradeSourcesSection(body) {
    // Find the H2 whose text content is "Sources" (case-insensitive, trimmed).
    const headings = body.querySelectorAll('h2');
    let sourcesHeading = null;
    headings.forEach(function (h) {
      if (h.textContent.trim().toLowerCase() === 'sources') sourcesHeading = h;
    });
    if (!sourcesHeading) return;
    sourcesHeading.classList.add('verity-sources-heading');

    // Walk subsequent siblings that look like source paragraphs.
    const list = document.createElement('ol');
    list.className = 'verity-sources-list';

    const toRemove = [];
    let sibling = sourcesHeading.nextElementSibling;
    while (sibling && sibling.tagName === 'P') {
      const text = sibling.textContent.trim();
      const match = text.match(SOURCE_LINE_RE);
      const fallback = text.match(SOURCE_LINE_FALLBACK_RE);
      if (!match && !fallback) break;
      list.appendChild(buildSourceItem(match, fallback));
      toRemove.push(sibling);
      sibling = sibling.nextElementSibling;
    }
    if (!list.children.length) return;

    sourcesHeading.insertAdjacentElement('afterend', list);
    toRemove.forEach(function (el) { el.remove(); });
  }

  function buildSourceItem(match, fallback) {
    // Prefer the structured match; fall back to title-only.
    const li = document.createElement('li');
    const idx = (match || fallback)[1];
    li.id = 'src' + idx;

    const wrap = document.createElement('div');

    if (match) {
      const titleSpan = document.createElement('span');
      titleSpan.className = 'vsrc-title';
      titleSpan.textContent = match[2].trim();
      wrap.appendChild(titleSpan);

      // The structured form already splits journal/year inside the title
      // tail (e.g. "Foo et al. — Bar baz baz (Journal, Year)"). We pull
      // the parenthesized "(Journal, Year)" tail into a cite line.
      const inner = match[2].match(/^(.*?)\s*\(([^()]+)\)\s*$/);
      if (inner) {
        titleSpan.textContent = inner[1].trim();
        const cite = document.createElement('span');
        cite.className = 'vsrc-cite';
        cite.textContent = inner[2].trim();
        wrap.appendChild(cite);
      }

      const link = document.createElement('a');
      link.className = 'vsrc-link';
      link.href = match[4];
      link.rel = 'noopener';
      link.textContent = humanizeUrl(match[4]);
      wrap.appendChild(link);

      li.appendChild(wrap);

      const chip = document.createElement('span');
      const type = (match[3] || '').toLowerCase();
      chip.className = 'eb-chip' + (type === 'guideline' || type === 'news' ? ' eb-chip--soft' : '');
      chip.textContent = type.replace(/^./, function (c) { return c.toUpperCase(); });
      li.appendChild(chip);
    } else {
      const titleSpan = document.createElement('span');
      titleSpan.className = 'vsrc-title';
      titleSpan.textContent = fallback[2].trim();
      wrap.appendChild(titleSpan);
      li.appendChild(wrap);
    }

    return li;
  }

  function humanizeUrl(raw) {
    return raw.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  // ---- (2) Inline citation linking ----
  function injectCitationLinks(body) {
    // Walk all text nodes inside <p> tags that aren't already linked.
    const paragraphs = body.querySelectorAll('p');
    paragraphs.forEach(function (p) {
      // Skip the disclaimer paragraphs — never inject inside Verity's own DSHEA blocks.
      if (p.classList.contains('verity-disclaimer') || p.closest('.verity-disclaimer')) return;
      processTextNodes(p);
    });
  }

  function processTextNodes(parent) {
    const nodes = [];
    parent.childNodes.forEach(function (n) {
      if (n.nodeType === Node.TEXT_NODE && CITATION_RE.test(n.nodeValue)) {
        CITATION_RE.lastIndex = 0;
        nodes.push(n);
      }
    });
    nodes.forEach(function (textNode) {
      const frag = document.createDocumentFragment();
      const text = textNode.nodeValue;
      let lastIndex = 0;
      let m;
      CITATION_RE.lastIndex = 0;
      while ((m = CITATION_RE.exec(text)) !== null) {
        if (m.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
        }
        const refs = m[1].split(',').map(function (s) { return s.trim(); });
        refs.forEach(function (n, i) {
          const sup = document.createElement('sup');
          const a = document.createElement('a');
          a.className = 'verity-citation';
          a.href = '#src' + n;
          a.textContent = '[' + n + ']';
          sup.appendChild(a);
          frag.appendChild(sup);
          if (i < refs.length - 1) frag.appendChild(document.createTextNode(','));
        });
        lastIndex = m.index + m[0].length;
      }
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      textNode.parentNode.replaceChild(frag, textNode);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
